/**
 * Single source of truth for case tracking event names and human-readable descriptions.
 * Used by the frontend (`trackEvent`), prod-monitor LLM prompts, and analytics tooling.
 *
 * Existing chat_* / widget_* analytics names are stable (PBI 35755).
 * Widget host listen names (`WIDGET_HOST_LISTEN_EVENT_NAMES`) are delivered via
 * `RepairIntelligence.onEvent` and mapped from those analytics events.
 * Bodewell / GE full-screen uses `JOURNEY_HOST_LISTEN_EVENT_NAMES` (PBI 35758).
 */

export const TRACKING_EVENT_DESCRIPTIONS = {
  chat_add_to_cart:
    "User adds a standard part to cart from the full chat experience.",
  chat_add_to_cart_early:
    "User adds an early surfaced compatible part to cart from the full chat experience.",
  chat_add_to_cart_suggested:
    "User adds a suggested part to cart from the full chat experience.",
  chat_click_cause:
    "User opens or selects a diagnostic cause from the full chat experience.",
  chat_contact_click:
    "User clicks the contact button from the full chat shell.",
  chat_resumed_from_external:
    "User opens the full chat route for an existing case from an external source (no in-app referrer), suggesting a return from a shared or saved link.",
  chat_continue_later_click:
    "User clicks the continue-later action (send to phone or save link) at the model number step in the full chat experience.",
  chat_continue_diagnosing_click:
    "User clicks Continue Diagnosing under an inline likely-part card in the full chat experience.",
  chat_diag_document_click:
    "User opens a diagnostic document attachment from the full chat experience.",
  chat_diag_image_click:
    "User opens a diagnostic image from the full chat experience.",
  chat_diag_video_click:
    "User opens a diagnostic video from the full chat experience.",
  chat_engaged:
    "Full chat conversation reaches engagement threshold after the second human message.",
  chat_find_pros:
    "User selects the find-a-pro option from remediation content in the full chat experience.",
  chat_message_sent: "User sends any message in the full chat experience.",
  chat_model_no_tip:
    "User clicks the model number tip from the full chat case header.",
  chat_order_return_status:
    "User clicks the unsupported-topic link for order or return status from the full chat route.",
  chat_other_cs_questions:
    "User clicks the unsupported-topic link for other customer service questions from the full chat route.",
  chat_part_no_lookup:
    "User clicks the unsupported-topic link for part number lookup from the full chat route.",
  chat_parts_found:
    "An ecommerce workflow in full chat surfaces one or more parts.",
  chat_parts_not_found:
    "An ecommerce workflow in full chat determines that no parts were found.",
  chat_quick_response_sent:
    "User sends a quick-response option in the full chat experience.",
  chat_save_case: "User clicks the save or copy link action for a chat case.",
  chat_see_options:
    "User selects the see-options remediation action from the full chat experience.",
  chat_shipping_return_questions:
    "User clicks the unsupported-topic link for shipping or return questions from the full chat route.",
  chat_show_me_how:
    "User selects the show-me-how remediation action from the full chat experience.",
  chat_outcome_diy:
    "Full chat renders a DIY repair recommendation for a confirmed cause or peek.",
  chat_outcome_pro_repair:
    "Full chat renders a pro service repair recommendation for a confirmed cause or peek.",
  chat_outcome_replace:
    "Full chat renders a replace appliance recommendation for a confirmed cause or peek.",
  chat_started: "A new full chat case is created and the conversation starts.",
  chat_typed_message_sent:
    "User sends a manually typed message in the full chat experience.",
  chat_upload_photo_click:
    "User clicks the upload photo action in the full chat experience.",
  chat_view_part: "User views part details from the full chat experience.",
  chat_workflow_customer_service_entered:
    "Full chat enters the customer service workflow.",
  chat_workflow_diagnosis_entered: "Full chat enters the diagnosis workflow.",
  chat_workflow_ecommerce_entered: "Full chat enters the ecommerce workflow.",
  chat_workflow_unsupported_entered:
    "Full chat enters the unsupported workflow.",
  diagnosis_probability_exceeds_60:
    "A diagnosis cause probability crosses the 60 percent tracking threshold.",
  diagnosis_probability_exceeds_90:
    "A diagnosis cause probability crosses the confirmation threshold near 90 percent.",
  graceful_offramp_accepted:
    "Customer accepted the graceful offramp and navigated to repair options.",
  graceful_offramp_continued:
    "Customer chose to continue diagnosing after a graceful offramp offer.",
  graceful_offramp_offered_skill_exceeded:
    "Graceful offramp offered because the next diagnostic step exceeded the customer's skill level.",
  graceful_offramp_offered_cannot_provide_info:
    "Graceful offramp offered because the customer could not provide required information.",
  graceful_offramp_offered_customer_declined:
    "Graceful offramp offered because the customer declined to continue diagnosing.",
  graceful_offramp_offered_low_confidence:
    "Graceful offramp offered because continued questioning was unlikely to produce a confident recommendation.",
  graceful_offramp_offered_monogram_trivial_parts:
    "Graceful offramp offered for Monogram because remaining diagnosis could not identify a trivial part.",
  diy_documents: "User opens the DIY documents tab.",
  diy_repair_guide: "User opens the DIY repair guide tab.",
  diy_videos: "User opens the DIY videos tab.",
  widget_add_to_cart:
    "User adds a standard part to cart from the widget experience.",
  widget_add_to_cart_early:
    "User adds an early surfaced compatible part to cart from the widget experience.",
  widget_add_to_cart_suggested:
    "User adds a suggested part to cart from the widget experience.",
  widget_chat_closed: "User closes the chat widget.",
  widget_chat_continue_later_click:
    "User clicks the continue-later action (send to phone or save link) at the model number step in the widget experience.",
  widget_chat_continue_diagnosing_click:
    "User clicks Continue Diagnosing under an inline likely-part card in the widget experience.",
  widget_chat_resumed_from_link:
    "Widget mounts with a case id carried in the URL hash, indicating the user returned to the conversation via a shared or saved link.",
  widget_chat_header_share_click:
    "User clicks the share button in the widget header to share the active case via the native share sheet.",
  widget_chat_diag_document_click:
    "User opens a diagnostic document attachment from the widget experience.",
  widget_chat_diag_image_click:
    "User opens a diagnostic image from the widget experience.",
  widget_chat_diag_video_click:
    "User opens a diagnostic video from the widget experience.",
  widget_chat_engaged:
    "Widget conversation reaches engagement threshold after the second human message.",
  widget_chat_fast_starter_fallback:
    "A starter bootstrap request in the widget falls back to the normal AI message pipeline instead of using the fast starter reply path.",
  widget_chat_fast_starter_used:
    "A starter bootstrap request in the widget uses the fast first-reply path with host-supplied compatible part data.",
  widget_chat_impression:
    "The chat widget is initialized and records an impression.",
  widget_chat_message_sent: "User sends any message in the widget experience.",
  widget_chat_minimized: "User minimizes the chat widget.",
  widget_chat_minimized_api:
    "The host page minimizes the chat widget through the public API.",
  widget_chat_new_conversation:
    "User starts a new conversation from the widget experience.",
  widget_chat_open: "User opens the chat widget.",
  widget_chat_open_api:
    "The host page opens the chat widget through the public API.",
  widget_chat_parts_found:
    "An ecommerce workflow in the widget surfaces one or more parts.",
  widget_chat_parts_not_found:
    "An ecommerce workflow in the widget determines that no parts were found.",
  widget_chat_quick_response_sent:
    "User sends a quick-response option in the widget experience.",
  widget_chat_started:
    "A new widget case is created and the conversation starts.",
  widget_chat_started_slideout:
    "A new widget case is created in the desktop slideout experience.",
  widget_chat_started_overlay:
    "A new widget case is created in the overlay experience (bubble/popup, typically mobile).",
  widget_chat_started_api:
    "A new widget conversation starts from a host-provided initial message.",
  widget_chat_started_starter:
    "A new widget conversation starts from a starter prompt.",
  widget_chat_started_typed:
    "A new widget conversation starts from a typed message.",
  widget_chat_starter_selected:
    "User selects a starter prompt before beginning a widget conversation.",
  widget_chat_starters_dismissed: "User dismisses the widget starter prompts.",
  widget_chat_starters_shown: "Widget starter prompts are shown to the user.",
  widget_chat_typed_message_sent:
    "User sends a manually typed message in the widget experience.",
  widget_chat_upload_photo_click:
    "User clicks the upload photo action in the widget experience.",
  widget_chat_workflow_customer_service_entered:
    "Widget chat enters the customer service workflow.",
  widget_chat_workflow_diagnosis_entered:
    "Widget chat enters the diagnosis workflow.",
  widget_chat_workflow_ecommerce_entered:
    "Widget chat enters the ecommerce workflow.",
  widget_chat_workflow_unsupported_entered:
    "Widget chat enters the unsupported workflow.",
  widget_view_part: "User views part details from the widget experience.",
  widget_view_part_early:
    "User views an early surfaced compatible part from the widget experience.",
  widget_view_suggested_part:
    "User views a suggested part from the widget experience.",
  // Widget host-listen unread (35758); analytics name matches onEvent listen name.
  chat_unread_changed:
    "The widget unread badge count changed, including to zero when the panel opens or the conversation is cleared.",
  // Bodewell / GE journey host-listen events (feature 35748 / PBI 35758).
  session_started: "Host opened the RI full-screen session.",
  case_created:
    "A new case entity exists (Bodewell host listen; pairs with analytics chat_started / widget_chat_started).",
  intake_updated:
    "An intake fact was collected (model, brand, appliance type, age months, or symptom).",
  intake_completed: "Intake facts are complete enough to begin diagnosis.",
  diagnosis_completed: "Diagnosis finished with at least one cause.",
  diagnosis_inconclusive:
    "Could not produce a usable diagnosis or recommendation.",
  cause_selected:
    "Customer clicked a diagnosed cause card in chat to open remediation options.",
  peek_selected:
    "Customer opened peek remediations from the price-range card or a chat option that routes to peek.",
  recommendation_previewed:
    "Early peek price ranges include a provisional DIY / Pro Service / Replace recommendation.",
  recommendation_generated:
    "DIY / Pro Service / Replace remediation cards were displayed to the customer.",
  added_to_cart:
    "Customer clicked Add to cart for a diagnosed or suggested part.",
  part_viewed:
    "Customer clicked Learn more / view part and navigated toward a part PDP.",
  next_step_selected:
    "Customer chose schedule service, replace appliance, or view product.",
  error_occurred: "Technical error or timeout during the session.",
  session_abandoned: "Session ended at teardown without a journey terminal.",
  session_closed: "Full-screen session is no longer presented (teardown).",
} as const

export type TrackEventName = keyof typeof TRACKING_EVENT_DESCRIPTIONS

/** All known event names (same order as keys in `TRACKING_EVENT_DESCRIPTIONS`). */
export const TRACK_EVENT_NAMES = Object.keys(
  TRACKING_EVENT_DESCRIPTIONS,
) as TrackEventName[]

export const getTrackingEventDescription = (eventName: string): string =>
  TRACKING_EVENT_DESCRIPTIONS[eventName as TrackEventName] ??
  `Tracking event "${eventName}" was recorded during the conversation.`

/** Widget embed host listen names (primitive payloads). Mapped from widget_* analytics. */
export const WIDGET_HOST_LISTEN_EVENT_NAMES = [
  "chat_opened",
  "chat_closed",
  "chat_started",
  "chat_unread_changed",
] as const

/** Bodewell / GE full-screen host listen names (object payloads). */
export const JOURNEY_HOST_LISTEN_EVENT_NAMES = [
  "session_started",
  "case_created",
  "intake_updated",
  "intake_completed",
  "diagnosis_completed",
  "diagnosis_inconclusive",
  "cause_selected",
  "peek_selected",
  "recommendation_previewed",
  "recommendation_generated",
  "added_to_cart",
  "part_viewed",
  "next_step_selected",
  "error_occurred",
  "session_abandoned",
  "session_closed",
] as const

export type WidgetHostListenEventName =
  (typeof WIDGET_HOST_LISTEN_EVENT_NAMES)[number]

export type JourneyHostListenEventName =
  (typeof JOURNEY_HOST_LISTEN_EVENT_NAMES)[number]

export const isWidgetHostListenEventName = (
  name: string,
): name is WidgetHostListenEventName =>
  (WIDGET_HOST_LISTEN_EVENT_NAMES as readonly string[]).includes(name)

export const isJourneyHostListenEventName = (
  name: string,
): name is JourneyHostListenEventName =>
  (JOURNEY_HOST_LISTEN_EVENT_NAMES as readonly string[]).includes(name)
